import React from "react";
import { DocumentIcon, FolderIcon, XIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import ProgressBar from "./ProgressBar";
import "./FileRow.css";

/**
 * Bun attachment row: type disc + name + meta, with an in-progress variant.
 * Spec: Component Library "File upload & attachment row" (also Vault).
 *
 *   <FileRow name="2025 tax return.pdf" type="pdf" meta="1.8 MB · Uploaded just now"
 *     onRemove={...} />
 *   <FileRow name="transactions-aug.csv" type="csv" progress={64} />
 *
 * Disc tints by file family (pdf danger, sheets success, images info,
 * everything else the neutral chip) — never pick ad-hoc colors.
 */
const TYPE_TONES = {
  pdf: "bg-bun-danger-bg text-bun-danger",
  doc: "bg-bun-info-bg text-bun-info",
  docx: "bg-bun-info-bg text-bun-info",
  folder: "bg-bun-warning-bg text-bun-warning",
  csv: "bg-bun-success-bg text-bun-success",
  xls: "bg-bun-success-bg text-bun-success",
  xlsx: "bg-bun-success-bg text-bun-success",
  png: "bg-bun-info-bg text-bun-info",
  jpg: "bg-bun-info-bg text-bun-info",
  jpeg: "bg-bun-info-bg text-bun-info",
  gif: "bg-bun-info-bg text-bun-info",
};

const DOCUMENT_COLUMNS = "lg:grid-cols-[minmax(0,1fr)_150px_140px_86px]";
const REVIEW_COLUMNS = "lg:grid-cols-[minmax(0,1fr)_150px_140px_86px_76px]";

export default function FileRow({ name, type, meta, progress, onRemove, onClick, className,
  variant = "attachment", actions, mobileActions, review, uploadedBy, date, status, nameContent, leading,
  selected, rowRef, disabled, ariaLabel, ...props }) {
  const ext = variant === "folder" ? "folder" : String(type || name?.split(".").pop() || "").toLowerCase();
  const uploading = progress != null;
  if (variant === "document" || variant === "folder") {
    const folder = variant === "folder";
    const mobileMetadata = (date || uploadedBy) && <span className="block lg:hidden text-bun-body font-book text-bun-stone">{date}{date && uploadedBy && ' · '}{uploadedBy}</span>;
    return (
      <div ref={rowRef} {...props} data-editing={Boolean(nameContent)} className={cn(
        "bun-file-row font-instrument text-bun-heading text-bun-text min-w-0",
        "grid items-center gap-x-1 px-[18px] py-2 border-b border-bun-line-soft last:border-b-0 lg:gap-x-3",
        review != null ? "grid-cols-[minmax(0,1fr)_auto_auto]" : "grid-cols-[minmax(0,1fr)_auto]",
        review != null ? REVIEW_COLUMNS : DOCUMENT_COLUMNS,
        selected ? "bg-bun-wash" : "hover:bg-bun-mist focus-within:bg-bun-mist",
        className
      )}>
        <div className="flex min-w-0 flex-1 items-center gap-[10px]">
          {leading}
          <span aria-hidden="true" className={cn("inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-bun lg:w-[44px]", folder ? "bg-bun-warning-bg text-bun-warning" : "bg-bun-mist text-bun-stone")}>
            {folder ? <FolderIcon className="h-5 w-5" /> : <DocumentIcon className="h-5 w-5" />}
          </span>
          <div className="min-w-0 flex-1">
            {nameContent || (onClick ? (
              <button type="button" onClick={onClick} disabled={disabled} aria-label={ariaLabel || name}
                className={cn("min-h-11 w-full text-left font-instrument text-bun-heading font-strong text-bun-text bg-transparent border-0 cursor-pointer focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue disabled:opacity-60", !folder && "lg:min-h-[30px]")}>
                <span className="block truncate">{name}</span>
                {folder && meta && <span className="block text-bun-body font-book text-bun-stone">{meta}</span>}
                {mobileMetadata}
              </button>
            ) : <span className="block truncate font-strong">{name}</span>)}
            {meta && (!folder || !onClick || nameContent) && <div className={cn("text-bun-body text-bun-stone", !folder && "hidden lg:block")}>{meta}</div>}
            {(!onClick || nameContent) && mobileMetadata}
            {uploading && <ProgressBar value={progress} className="mt-1" />}
          </div>
          {status}
        </div>
        <div className="hidden lg:block text-bun-body font-book text-bun-stone">{date}</div>
        <div className="hidden lg:block min-w-0 text-bun-body text-bun-slate truncate">{uploadedBy}</div>
        <div className="bun-file-row-actions ml-auto flex shrink-0 items-center justify-end gap-1">
          {mobileActions ? <><div className="lg:hidden">{mobileActions}</div><div className="hidden lg:flex items-center gap-1">{actions}</div></> : actions}
        </div>
        {review != null && <div className="flex items-center justify-center">{review}</div>}
      </div>
    );
  }
  return (
    <div
      {...(onClick && {
        onClick,
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e);
          }
        },
      })}
      className={cn(
        "flex items-center gap-[10px] border border-bun-line rounded-bun-lg px-3 py-[10px] font-instrument",
        onClick &&
          "cursor-pointer hover:bg-bun-mist focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue",
        className
      )}
    >
      <span
        className={cn(
          "w-[30px] h-[30px] rounded-[8px] shrink-0 inline-flex items-center justify-center",
          "text-[9px] font-semibold uppercase",
          TYPE_TONES[ext] || "bg-bun-chip-bg text-bun-chip-text"
        )}
      >
        {ext.slice(0, 4) || "file"}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-bun-body font-strong text-bun-text truncate">{name}</div>
        {uploading ? (
          <ProgressBar value={progress} className="mt-[6px] h-[3px]" />
        ) : (
          meta && <div className="text-bun-label tracking-normal font-book text-bun-faint">{meta}</div>
        )}
      </div>
      {uploading ? (
        <span className="text-bun-label tracking-normal font-strong text-bun-stone shrink-0">
          {Math.round(progress)}%
        </span>
      ) : (
        onRemove && (
          <button
            type="button"
            aria-label={`Remove ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(e);
            }}
            className="min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 items-center justify-center p-1 border-0 bg-transparent text-bun-faint hover:text-bun-text cursor-pointer inline-flex shrink-0 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue"
          >
            <XIcon className="w-[13px] h-[13px]" />
          </button>
        )
      )}
    </div>
  );
}

FileRow.Header = function FileRowHeader({ name, uploadedBy, date, actions, review }) {
  return <div className={cn("hidden items-center gap-3 rounded-t-bun-lg border-b border-bun-line-soft bg-bun-canvas px-[18px] py-2 font-instrument text-bun-meta font-semibold uppercase tracking-wide text-bun-faint lg:grid", review != null ? REVIEW_COLUMNS : DOCUMENT_COLUMNS)}>
    <div>{name}</div><div>{date}</div><div className="whitespace-nowrap">{uploadedBy}</div><div>{actions}</div>
    {review != null && <div className="text-center">{review}</div>}
  </div>;
};
